import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { DebugUiBridge } from '../DebugUiBridge';

/**
 * Post-Processing Panel
 * 
 * Standalone Lit component for post-processing effect controls.
 * Includes pixelation, pixel size, pixel animation, and god rays.
 */
@customElement('post-processing-panel')
export class PostProcessingPanel extends LitElement {
  @property({ type: Boolean }) pixelationEnabled = false;
  @property({ type: Number }) pixelSize = 4;
  @property({ type: Boolean }) pixelAnimateEnabled = false;
  @property({ type: Boolean }) godRaysEnabled = false;
  @property({ type: Boolean }) chromaticAberrationEnabled = false;
  @property({ type: Number }) chromaticAberrationAmount = 12;
  @property({ type: Number }) chromaticAberrationRadialIntensity = 1.0;
  
  // Lens effect properties
  @property({ type: Boolean }) lensEnabled = false;
  @property({ type: Number }) lensEdgeBlur = 0.0;
  @property({ type: Number }) lensChromaticAberration = 0.0;
  @property({ type: Number }) lensDistortion = 0.0;
  @property({ type: Number }) lensGrain = 0.0;
  @property({ type: Boolean }) lensDofEnabled = false;
  @property({ type: Number }) lensDofDistance = 50;
  @property({ type: Number }) lensDofAperture = 0.03;  // Low = subtle, High = strong blur
  @property({ type: Number }) lensDofGain = 1.0;  // Bokeh highlight intensity
  @property({ type: Boolean }) lensDofAutoFocus = true;
  
  // Fog properties
  @property({ type: Boolean }) fogEnabled = false;
  @property({ type: Number }) fogDensity = 0.02;
  @property({ type: Number }) fogColorR = 0.5;
  @property({ type: Number }) fogColorG = 0.5;
  @property({ type: Number }) fogColorB = 0.6;
  
  static styles = css`
    :host {
      display: block;
      color: white;
      font-family: ui-sans-serif, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 13px;
      padding: 16px;
    }
    
    .control-group {
      margin-bottom: 20px;
      padding-bottom: 16px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }
    
    .control-group:last-of-type {
      border-bottom: none;
    }
    
    h3 {
      margin: 0 0 12px 0;
      font-size: 14px;
      font-weight: 600;
      opacity: 0.95;
    }
    
    label {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 0;
      cursor: pointer;
      user-select: none;
    }
    
    label:hover {
      background: rgba(255, 255, 255, 0.05);
      border-radius: 4px;
      padding-left: 4px;
    }
    
    input[type="checkbox"] {
      width: 16px;
      height: 16px;
      cursor: pointer;
    }
    
    .slider-row {
      display: flex;
      align-items: center;
      gap: 12px;
      margin: 12px 0;
    }
    
    .slider-row label {
      min-width: 80px;
      opacity: 0.75;
      font-size: 12px;
      cursor: default;
      padding: 0;
    }
    
    .slider-row label:hover {
      background: none;
      padding: 0;
    }
    
    input[type="range"] {
      flex: 1;
      height: 24px;
      cursor: pointer;
    }
    
    .value {
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      font-size: 12px;
      min-width: 30px;
      text-align: right;
      opacity: 0.95;
    }
    
    button {
      padding: 8px 16px;
      background: rgba(255, 255, 255, 0.08);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 6px;
      color: white;
      font-size: 12px;
      cursor: pointer;
      transition: all 0.15s;
    }
    
    button:hover {
      background: rgba(255, 255, 255, 0.12);
      border-color: rgba(255, 255, 255, 0.3);
    }
    
    button:active {
      background: rgba(255, 255, 255, 0.15);
    }
    
    button.active {
      background: rgba(124, 252, 144, 0.15);
      border-color: rgba(124, 252, 144, 0.4);
      color: #7CFC90;
    }
  `;
  
  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('himum:debug-ui-event', this.onDebugEvent as EventListener);
  }
  
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('himum:debug-ui-event', this.onDebugEvent as EventListener);
  }
  
  private onDebugEvent = (e: CustomEvent) => {
    const event = e.detail;
    
    switch (event.type) {
      case 'postprocess:pixelation:changed':
        this.pixelationEnabled = event.enabled;
        break;
      case 'postprocess:pixelSize:changed':
        this.pixelSize = event.value;
        break;
      case 'postprocess:pixelAnimate:changed':
        this.pixelAnimateEnabled = event.enabled;
        break;
      case 'postprocess:godRays:changed':
        this.godRaysEnabled = event.enabled;
        break;

      case 'postprocess:chromaticAberration:changed':
        this.chromaticAberrationEnabled = event.enabled;
        break;

      case 'postprocess:chromaticAberration:amount:changed':
        this.chromaticAberrationAmount = event.value;
        break;

      case 'postprocess:chromaticAberration:radialIntensity:changed':
        this.chromaticAberrationRadialIntensity = event.value;
        break;
        
      // Lens effect events
      case 'postprocess:lens:changed':
        this.lensEnabled = event.enabled;
        break;
      case 'postprocess:lens:config:changed':
        if (event.config) {
          this.lensEdgeBlur = event.config.edge_blur ?? this.lensEdgeBlur;
          this.lensChromaticAberration = event.config.chromatic_aberration ?? this.lensChromaticAberration;
          this.lensDistortion = event.config.distortion ?? this.lensDistortion;
          this.lensGrain = event.config.grain_amount ?? this.lensGrain;
          this.lensDofDistance = event.config.dof_focus_distance ?? this.lensDofDistance;
          this.lensDofAperture = event.config.dof_aperture ?? this.lensDofAperture;
          this.lensDofGain = event.config.dof_gain ?? this.lensDofGain;
        }
        break;
      case 'postprocess:lens:dofAutoFocus:changed':
        this.lensDofAutoFocus = event.enabled;
        break;
        
      // Fog events
      case 'environment:fog:changed':
        this.fogEnabled = event.enabled;
        this.fogDensity = event.density ?? this.fogDensity;
        if (event.color) {
          this.fogColorR = event.color.r;
          this.fogColorG = event.color.g;
          this.fogColorB = event.color.b;
        }
        break;
    }
  };
  
  private togglePixelation() {
    this.pixelationEnabled = !this.pixelationEnabled;
    DebugUiBridge.dispatchCommand({
      type: 'postprocess:pixelation:set',
      enabled: this.pixelationEnabled
    });
  }
  
  private togglePixelAnimate() {
    this.pixelAnimateEnabled = !this.pixelAnimateEnabled;
    DebugUiBridge.dispatchCommand({
      type: 'postprocess:pixelAnimate:set',
      enabled: this.pixelAnimateEnabled
    });
  }
  
  private toggleGodRays() {
    this.godRaysEnabled = !this.godRaysEnabled;
    DebugUiBridge.dispatchCommand({
      type: 'postprocess:godRays:set',
      enabled: this.godRaysEnabled
    });
  }

  private toggleChromaticAberration() {
    this.chromaticAberrationEnabled = !this.chromaticAberrationEnabled;
    DebugUiBridge.dispatchCommand({
      type: 'postprocess:chromaticAberration:set',
      enabled: this.chromaticAberrationEnabled,
    });
  }

  private onChromaticAberrationAmountChange(e: Event) {
    const value = parseFloat((e.target as HTMLInputElement).value);
    const clamped = Math.max(0, Math.min(50, value));
    this.chromaticAberrationAmount = clamped;
    DebugUiBridge.dispatchCommand({
      type: 'postprocess:chromaticAberration:amount:set',
      value: clamped,
    });
  }

  private onChromaticAberrationRadialIntensityChange(e: Event) {
    const value = parseFloat((e.target as HTMLInputElement).value);
    const clamped = Math.max(0, Math.min(5, value));
    this.chromaticAberrationRadialIntensity = clamped;
    DebugUiBridge.dispatchCommand({
      type: 'postprocess:chromaticAberration:radialIntensity:set',
      value: clamped,
    });
  }
  
  // Lens effect methods
  private toggleLens() {
    this.lensEnabled = !this.lensEnabled;
    DebugUiBridge.dispatchCommand({
      type: 'postprocess:lens:set',
      enabled: this.lensEnabled
    });
  }
  
  private onLensEdgeBlurChange(e: Event) {
    const value = parseFloat((e.target as HTMLInputElement).value);
    this.lensEdgeBlur = value;
    DebugUiBridge.dispatchCommand({ type: 'postprocess:lens:edgeBlur:set', value });
  }
  
  private onLensChromaticAberrationChange(e: Event) {
    const value = parseFloat((e.target as HTMLInputElement).value);
    this.lensChromaticAberration = value;
    DebugUiBridge.dispatchCommand({ type: 'postprocess:lens:chromaticAberration:set', value });
  }
  
  private onLensDistortionChange(e: Event) {
    const value = parseFloat((e.target as HTMLInputElement).value);
    this.lensDistortion = value;
    DebugUiBridge.dispatchCommand({ type: 'postprocess:lens:distortion:set', value });
  }
  
  private onLensGrainChange(e: Event) {
    const value = parseFloat((e.target as HTMLInputElement).value);
    this.lensGrain = value;
    DebugUiBridge.dispatchCommand({ type: 'postprocess:lens:grain:set', value });
  }
  
  private onLensDofDistanceChange(e: Event) {
    const value = parseFloat((e.target as HTMLInputElement).value);
    this.lensDofDistance = value;
    DebugUiBridge.dispatchCommand({ type: 'postprocess:lens:dofDistance:set', value });
  }
  
  private onLensDofApertureChange(e: Event) {
    const value = parseFloat((e.target as HTMLInputElement).value);
    this.lensDofAperture = value;
    DebugUiBridge.dispatchCommand({ type: 'postprocess:lens:dofAperture:set', value });
  }
  
  private onLensDofGainChange(e: Event) {
    const value = parseFloat((e.target as HTMLInputElement).value);
    this.lensDofGain = value;
    DebugUiBridge.dispatchCommand({ type: 'postprocess:lens:dofGain:set', value });
  }
  
  private toggleDofAutoFocus() {
    this.lensDofAutoFocus = !this.lensDofAutoFocus;
    DebugUiBridge.dispatchCommand({ type: 'postprocess:lens:dofAutoFocus:set', enabled: this.lensDofAutoFocus });
  }
  
  private toggleDofEnabled() {
    this.lensDofEnabled = !this.lensDofEnabled;
    DebugUiBridge.dispatchCommand({ type: 'postprocess:lens:dofEnabled:set', enabled: this.lensDofEnabled });
  }
  
  // Fog methods
  private toggleFog() {
    this.fogEnabled = !this.fogEnabled;
    DebugUiBridge.dispatchCommand({ type: 'environment:fog:set', enabled: this.fogEnabled });
  }
  
  private onFogDensityChange(e: Event) {
    const value = parseFloat((e.target as HTMLInputElement).value);
    this.fogDensity = value;
    DebugUiBridge.dispatchCommand({ type: 'environment:fog:density:set', value });
  }
  
  private onFogColorRChange(e: Event) {
    const value = parseFloat((e.target as HTMLInputElement).value);
    this.fogColorR = value;
    DebugUiBridge.dispatchCommand({ 
      type: 'environment:fog:color:set', 
      r: this.fogColorR, 
      g: this.fogColorG, 
      b: this.fogColorB 
    });
  }
  
  private onFogColorGChange(e: Event) {
    const value = parseFloat((e.target as HTMLInputElement).value);
    this.fogColorG = value;
    DebugUiBridge.dispatchCommand({ 
      type: 'environment:fog:color:set', 
      r: this.fogColorR, 
      g: this.fogColorG, 
      b: this.fogColorB 
    });
  }
  
  private onFogColorBChange(e: Event) {
    const value = parseFloat((e.target as HTMLInputElement).value);
    this.fogColorB = value;
    DebugUiBridge.dispatchCommand({ 
      type: 'environment:fog:color:set', 
      r: this.fogColorR, 
      g: this.fogColorG, 
      b: this.fogColorB 
    });
  }
  
  private onPixelSizeChange(e: Event) {
    const value = parseInt((e.target as HTMLInputElement).value, 10);
    const clamped = Math.max(1, Math.min(8, value));
    this.pixelSize = clamped;
    DebugUiBridge.dispatchCommand({
      type: 'postprocess:pixelSize:set',
      value: clamped
    });
  }
  
  render() {
    return html`
      <div class="control-group">
        <h3>Pixelation</h3>
        <label>
          <input
            type="checkbox"
            ?checked=${this.pixelationEnabled}
            @change=${this.togglePixelation}
          />
          Enable Pixelation
        </label>
        
        <div class="slider-row">
          <label>Pixel Size</label>
          <input
            type="range"
            min="1"
            max="8"
            step="1"
            .value=${String(this.pixelSize)}
            @input=${this.onPixelSizeChange}
          />
          <span class="value">${this.pixelSize}</span>
        </div>
        
        <label>
          <input
            type="checkbox"
            ?checked=${this.pixelAnimateEnabled}
            @change=${this.togglePixelAnimate}
          />
          Animate Pixels
        </label>
      </div>
      
      <div class="control-group">
        <h3>God Rays</h3>
        <label>
          <input
            type="checkbox"
            ?checked=${this.godRaysEnabled}
            @change=${this.toggleGodRays}
          />
          Enable God Rays
        </label>
        <p style="opacity: 0.5; font-size: 11px; margin: 8px 0 0 0;">
          Per-mesh params in Explorer panel
        </p>
      </div>

      <div class="control-group">
        <h3>Chromatic Aberration</h3>
        <label>
          <input
            type="checkbox"
            ?checked=${this.chromaticAberrationEnabled}
            @change=${this.toggleChromaticAberration}
          />
          Enable Chromatic Aberration
        </label>

        <div class="slider-row">
          <label>Amount</label>
          <input
            type="range"
            min="0"
            max="50"
            step="0.5"
            .value=${String(this.chromaticAberrationAmount)}
            @input=${this.onChromaticAberrationAmountChange}
          />
          <span class="value">${this.chromaticAberrationAmount.toFixed(1)}</span>
        </div>

        <div class="slider-row">
          <label>Edge</label>
          <input
            type="range"
            min="0"
            max="5"
            step="0.05"
            .value=${String(this.chromaticAberrationRadialIntensity)}
            @input=${this.onChromaticAberrationRadialIntensityChange}
          />
          <span class="value">${this.chromaticAberrationRadialIntensity.toFixed(2)}</span>
        </div>
      </div>
      
      <div class="control-group">
        <h3>Lens Effect</h3>
        <label>
          <input
            type="checkbox"
            ?checked=${this.lensEnabled}
            @change=${this.toggleLens}
          />
          Enable Lens Effect
        </label>
        
        <div class="slider-row">
          <label>Edge Blur</label>
          <input
            type="range"
            min="0"
            max="2"
            step="0.05"
            .value=${String(this.lensEdgeBlur)}
            @input=${this.onLensEdgeBlurChange}
            ?disabled=${!this.lensEnabled}
          />
          <span class="value">${this.lensEdgeBlur.toFixed(2)}</span>
        </div>
        
        <div class="slider-row">
          <label>Chromatic</label>
          <input
            type="range"
            min="0"
            max="2"
            step="0.05"
            .value=${String(this.lensChromaticAberration)}
            @input=${this.onLensChromaticAberrationChange}
            ?disabled=${!this.lensEnabled}
          />
          <span class="value">${this.lensChromaticAberration.toFixed(2)}</span>
        </div>
        
        <div class="slider-row">
          <label>Distortion</label>
          <input
            type="range"
            min="0"
            max="2"
            step="0.05"
            .value=${String(this.lensDistortion)}
            @input=${this.onLensDistortionChange}
            ?disabled=${!this.lensEnabled}
          />
          <span class="value">${this.lensDistortion.toFixed(2)}</span>
        </div>
        
        <div class="slider-row">
          <label>Grain</label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.02"
            .value=${String(this.lensGrain)}
            @input=${this.onLensGrainChange}
            ?disabled=${!this.lensEnabled}
          />
          <span class="value">${this.lensGrain.toFixed(2)}</span>
        </div>
        
        <h4 style="margin: 16px 0 8px 0; font-size: 12px; opacity: 0.8;">Depth of Field</h4>
        
        <label>
          <input
            type="checkbox"
            ?checked=${this.lensDofEnabled}
            @change=${this.toggleDofEnabled}
            ?disabled=${!this.lensEnabled}
          />
          Enable DoF
        </label>
        
        <div class="slider-row">
          <label>Focus Dist</label>
          <input
            type="range"
            min="1"
            max="200"
            step="1"
            .value=${String(this.lensDofDistance)}
            @input=${this.onLensDofDistanceChange}
            ?disabled=${!this.lensEnabled || !this.lensDofEnabled || this.lensDofAutoFocus}
          />
          <span class="value">${this.lensDofDistance.toFixed(0)}</span>
        </div>
        
        <label style="margin-top: 8px;">
          <input
            type="checkbox"
            ?checked=${this.lensDofAutoFocus}
            @change=${this.toggleDofAutoFocus}
            ?disabled=${!this.lensEnabled || !this.lensDofEnabled}
          />
          Auto-Focus Player
        </label>
        
        <div class="slider-row">
          <label>Blur Amount</label>
          <input
            type="range"
            min="0.01"
            max="1.0"
            step="0.01"
            .value=${String(this.lensDofAperture)}
            @input=${this.onLensDofApertureChange}
            ?disabled=${!this.lensEnabled || !this.lensDofEnabled}
          />
          <span class="value">${this.lensDofAperture.toFixed(2)}</span>
        </div>
        <p style="opacity: 0.5; font-size: 10px; margin: 4px 0 0 0;">
          Higher = more blur (aperture)
        </p>
        
        <div class="slider-row">
          <label>Bokeh</label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.02"
            .value=${String(this.lensDofGain)}
            @input=${this.onLensDofGainChange}
            ?disabled=${!this.lensEnabled || !this.lensDofEnabled}
          />
          <span class="value">${this.lensDofGain.toFixed(2)}</span>
        </div>
      </div>
      
      <div class="control-group">
        <h3>Fog</h3>
        <label>
          <input
            type="checkbox"
            ?checked=${this.fogEnabled}
            @change=${this.toggleFog}
          />
          Enable Fog
        </label>
        
        <div class="slider-row">
          <label>Density</label>
          <input
            type="range"
            min="0.001"
            max="0.1"
            step="0.001"
            .value=${String(this.fogDensity)}
            @input=${this.onFogDensityChange}
            ?disabled=${!this.fogEnabled}
          />
          <span class="value">${this.fogDensity.toFixed(3)}</span>
        </div>
        
        <div class="slider-row">
          <label>Color R</label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            .value=${String(this.fogColorR)}
            @input=${this.onFogColorRChange}
            ?disabled=${!this.fogEnabled}
          />
          <span class="value">${this.fogColorR.toFixed(2)}</span>
        </div>
        
        <div class="slider-row">
          <label>Color G</label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            .value=${String(this.fogColorG)}
            @input=${this.onFogColorGChange}
            ?disabled=${!this.fogEnabled}
          />
          <span class="value">${this.fogColorG.toFixed(2)}</span>
        </div>
        
        <div class="slider-row">
          <label>Color B</label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            .value=${String(this.fogColorB)}
            @input=${this.onFogColorBChange}
            ?disabled=${!this.fogEnabled}
          />
          <span class="value">${this.fogColorB.toFixed(2)}</span>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'post-processing-panel': PostProcessingPanel;
  }
}
